import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { getCachedPreview, fetchAndCachePreview } from "@/lib/smartReviewCache";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Target, AlertTriangle, TrendingUp, Trophy, Loader2, ArrowRight, CloudOff, CloudDownload, RotateCcw } from "lucide-react";
import { toast } from "sonner";

export default function SmartReviewPage() {
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);
  const [cachedCount, setCachedCount] = useState(0);
  const [cachingNow, setCachingNow] = useState(false);
  const [activeSession, setActiveSession] = useState(null); // Phase 47 — resume

  const load = () => api.get("/student/smart-review/summary").then((r) => setSummary(r.data));
  useEffect(() => {
    load();
    getCachedPreview().then((p) => setCachedCount(p?.questions?.length || 0)).catch(() => {});
    // Phase 47 — check for an in-progress session to enable Resume
    api.get("/student/smart-review/active")
      .then((r) => { if (r.data?.has_active) setActiveSession(r.data); })
      .catch(() => {});
  }, []);

  const resume = async () => {
    if (!activeSession) return;
    setLoading(true);
    try {
      const r = await api.get(`/student/smart-review/${activeSession.session_id}`);
      if (r.data.status === "completed") {
        toast.info("That session already finished — starting a new one!");
        setActiveSession(null);
        return;
      }
      sessionStorage.setItem("pm_smart_session", JSON.stringify({
        session_id: r.data.session_id,
        questions: r.data.questions,
        total: r.data.total_questions,
      }));
      sessionStorage.setItem("pm_smart_resume_idx", String(r.data.current_idx || 0));
      sessionStorage.setItem("pm_smart_resume_answered",
        JSON.stringify(r.data.answered_question_ids || []));
      nav("/student/smart-review/session");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not resume session");
    } finally {
      setLoading(false);
    }
  };

  const cacheForOffline = async () => {
    setCachingNow(true);
    try {
      const d = await fetchAndCachePreview(15);
      setCachedCount(d?.questions?.length || 0);
      toast.success(`Cached ${d?.questions?.length || 0} questions for offline practice`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not cache questions");
    } finally { setCachingNow(false); }
  };

  const start = async () => {
    setLoading(true);
    try {
      const r = await api.post("/student/smart-review/start", {});
      sessionStorage.setItem("pm_smart_session", JSON.stringify(r.data));
      nav("/student/smart-review/session");
    } catch (e) {
      setLoading(false);
    }
  };

  const generateInsight = async () => {
    setInsightLoading(true);
    try {
      await api.post("/student/smart-review/generate-insight");
      toast.success("AI insight generated");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not generate insight");
    } finally {
      setInsightLoading(false);
    }
  };

  if (!summary) return <div className="text-slate-500">Loading…</div>;

  const ai = summary.ai_insight;

  return (
    <div className="space-y-6" data-testid="smart-review-page">
      <div>
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-indigo-600 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" /> Smart Review
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-1">Personalized for you</h1>
        <p className="text-slate-600 mt-1">Questions chosen based on your learning progress — focus on what needs work.</p>
      </div>

      {/* Phase 47 — Resume in-progress session card */}
      {activeSession && (
        <Card
          className="p-4 sm:p-5 border-amber-300 bg-gradient-to-r from-amber-50 to-orange-50 flex items-center gap-4"
          data-testid="smart-review-resume-card"
        >
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-amber-500 to-orange-500 text-white grid place-items-center shrink-0">
            <RotateCcw className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-display font-bold text-base text-amber-900">
              Resume your in-progress session
            </div>
            <div className="text-xs text-amber-800/90 mt-0.5">
              You&apos;re on question {(activeSession.current_idx || 0) + 1} of {activeSession.total_questions} ·
              <span className="ml-1">{activeSession.answered_count} answered</span>
            </div>
          </div>
          <Button
            onClick={resume}
            disabled={loading}
            className="bg-gradient-to-r from-amber-500 to-orange-500 hover:opacity-90 text-white shadow shrink-0"
            data-testid="smart-review-resume-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RotateCcw className="w-4 h-4 mr-1.5" />}
            Resume
          </Button>
        </Card>
      )}

      {!summary.has_data ? (
        <Card className="p-10 text-center border-slate-200" data-testid="empty-state">
          <div className="mx-auto h-14 w-14 rounded-full bg-indigo-100 grid place-items-center text-indigo-600 mb-4">
            <Sparkles className="w-7 h-7" />
          </div>
          <h2 className="font-display text-xl font-bold">Not enough data yet</h2>
          <p className="text-slate-600 mt-2 max-w-md mx-auto">Answer at least 5 questions in Exam Mode so Smart Review can personalize your sessions. Once ready, you'll get AI insights and a tailored review.</p>
          <div className="mt-6 flex items-center justify-center gap-3 flex-wrap">
            <Link to="/student/courses"><Button className="bg-[#1E3A8A] hover:bg-[#1E40AF]">Browse Courses</Button></Link>
            <Button variant="outline" onClick={generateInsight} disabled={insightLoading} data-testid="student-generate-insight-btn">
              {insightLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate AI Insight</>}
            </Button>
          </div>
        </Card>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[
              { icon: Target, label: "Due Today", value: summary.due_today, color: "indigo", testId: "stat-due" },
              { icon: AlertTriangle, label: "Repeatedly Missed", value: summary.repeatedly_missed, color: "amber" },
              { icon: TrendingUp, label: "Accuracy", value: `${summary.accuracy}%`, color: "blue" },
              { icon: Trophy, label: "Mastered", value: summary.mastered, color: "green" },
            ].map((s, i) => (
              <Card key={i} className="p-5 border-slate-200">
                <div className={`h-9 w-9 rounded-md grid place-items-center bg-${s.color}-50 text-${s.color}-700`}><s.icon className="w-5 h-5" strokeWidth={1.5} /></div>
                <div className="mt-4 text-3xl font-display font-bold">{s.value}</div>
                <div className="text-sm text-slate-500 mt-0.5">{s.label}</div>
              </Card>
            ))}
          </div>

          {summary.chronic_miss > 0 && (
            <Card className="p-5 border-amber-200 bg-amber-50" data-testid="chronic-miss-callout">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-xl grid place-items-center bg-amber-200 text-amber-800 shrink-0">
                  <AlertTriangle className="w-5 h-5" strokeWidth={2} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-sm text-amber-900">
                    {summary.chronic_miss} question{summary.chronic_miss === 1 ? "" : "s"} need extra attention
                  </div>
                  <p className="text-xs text-amber-800 mt-0.5 leading-relaxed">
                    You've missed these multiple times — Smart Review is now prioritising them automatically.
                  </p>
                </div>
              </div>
            </Card>
          )}

          <Card className="p-6 border-slate-200 bg-gradient-to-br from-indigo-50 to-blue-50">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <h3 className="font-display font-bold text-xl flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-indigo-600" /> Start Today's Smart Review
                </h3>
                <p className="text-sm text-slate-600 mt-1">15 questions prioritized for your weak areas.</p>
                <div className="mt-3 flex flex-wrap gap-2">
                  {summary.weakest_subject && <Badge variant="outline" className="bg-white">Weak Subject: {summary.weakest_subject.name} ({summary.weakest_subject.accuracy}%)</Badge>}
                  {summary.weakest_module && <Badge variant="outline" className="bg-white">Weak Module: {summary.weakest_module.name} ({summary.weakest_module.accuracy}%)</Badge>}
                </div>
              </div>
              <Button onClick={start} disabled={loading} className="bg-[#1E3A8A] hover:bg-[#1E40AF] h-11 px-6" data-testid="start-smart-review-btn">
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Start Smart Review <ArrowRight className="w-4 h-4 ml-2" /></>}
              </Button>
            </div>
          </Card>

          <Card className="p-5 border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50" data-testid="offline-practice-card">
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="flex-1 min-w-0">
                <h3 className="font-display font-bold text-base flex items-center gap-2">
                  <CloudOff className="w-4 h-4 text-amber-700" /> Offline Practice
                </h3>
                <p className="text-sm text-slate-700 mt-1">
                  {cachedCount > 0
                    ? <>You have <span className="font-bold">{cachedCount}</span> cached questions ready to study without internet. Answers sync the moment you reconnect.</>
                    : "Cache a study set now so you can practice on the MRT, jeepney, or anywhere without Wi-Fi."}
                </p>
              </div>
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant="outline"
                  className="border-amber-300 text-amber-800 hover:bg-amber-100"
                  onClick={cacheForOffline}
                  disabled={cachingNow}
                  data-testid="cache-for-offline-btn"
                >
                  {cachingNow ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <CloudDownload className="w-4 h-4 mr-1.5" />}
                  {cachedCount > 0 ? "Refresh cache" : "Cache for offline"}
                </Button>
                {cachedCount > 0 && (
                  <Button
                    onClick={() => nav("/student/smart-review/offline")}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="start-offline-practice-btn"
                  >
                    Start Offline Practice <ArrowRight className="w-4 h-4 ml-1.5" />
                  </Button>
                )}
              </div>
            </div>
          </Card>

          {ai ? (
            <Card className="p-6 border-slate-200 ai-card">
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div className="text-xs font-bold text-indigo-700 uppercase tracking-wide flex items-center gap-2"><Sparkles className="w-3.5 h-3.5" /> AI Learning Insight</div>
                <Button size="sm" variant="outline" onClick={generateInsight} disabled={insightLoading} data-testid="student-generate-insight-btn">
                  {insightLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Regenerate</>}
                </Button>
              </div>
              <p className="mt-2 text-sm leading-relaxed text-slate-800">{ai.insight_text}</p>
              {ai.data?.recommended_action && (
                <div className="mt-3 text-sm">
                  <span className="text-xs font-semibold uppercase tracking-wide text-indigo-700">Recommended action:</span>{" "}
                  <span className="text-slate-700">{ai.data.recommended_action}</span>
                </div>
              )}
              {ai.data?.topics_to_review && ai.data.topics_to_review.length > 0 && (
                <div className="mt-3">
                  <div className="text-xs font-semibold uppercase tracking-wide text-indigo-700 mb-1">Topics to review</div>
                  <div className="flex flex-wrap gap-1.5">
                    {ai.data.topics_to_review.map((t) => <Badge key={t} variant="outline" className="bg-white">{t}</Badge>)}
                  </div>
                </div>
              )}
              <div className="mt-3 text-xs text-slate-500">Generated {ai.generated_at?.slice(0, 10)}</div>
            </Card>
          ) : (
            <Card className="p-6 border-slate-200 border-dashed text-center">
              <Sparkles className="w-7 h-7 mx-auto text-indigo-500 mb-2" />
              <h3 className="font-display font-bold">No AI insight yet</h3>
              <p className="text-sm text-slate-600 mt-1 mb-4">Generate an AI-powered analysis of your weak areas and recommended topics.</p>
              <Button onClick={generateInsight} disabled={insightLoading} className="bg-[#1E3A8A] hover:bg-[#1E40AF]" data-testid="student-generate-insight-btn">
                {insightLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1" /> Generate AI Insight</>}
              </Button>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
